/**
 * G009 — 电力网络。
 *
 * 设计取舍见 docs/goals/G009-design-notes.md，四条要点在此复述，因为它们
 * 决定了本文件里几乎每个看起来"多余"的写法：
 *
 * 1. **电力不是库存**。每 Tick 生产、每 Tick 消耗、不结转，因此不进
 *    `resources.stock`。唯一能跨 Tick 携带电量的是储能，且它有**功率上限**
 *    （每 Tick 最多充/放多少）与**容量上限**（总共能存多少）两个不同约束。
 * 2. **一切用整数瓦**。浮点加法对顺序敏感，整数不敏感 —— 这直接消除了
 *    「同输入不同遍历顺序得不同结果」最隐蔽的来源。
 * 3. **分量划分与顺序无关**。并查集的最终划分本就与 union 顺序无关；唯一
 *    受顺序影响的是"代表元是谁"，故代表元取分量内**字典序最小的键**。
 * 4. **缓存不进 WorldState**。脏标志进状态会污染哈希，且 `fromSnapshot` 走
 *    structuredClone 不执行初始化 —— 恢复后标志与进程内缓存必然失配。改为
 *    状态里只存 `topologyVersion`（领域事实），缓存在模块作用域并记录它是为
 *    哪个版本建的。
 */

import { cellKey, type BuildCellId } from '../spatial/types.js';
import { getCell } from '../spatial/grid.js';
import type { SpatialMapData } from '../spatial/types.js';
import type { BuildingComponent } from '../building/plan-building.js';
import {
  networkTopology, clearNetworkCache, operationalBuildings,
  type EntityTableLike, type NetworkComponent, type NetworkTopology,
} from '../utility/network.js';
import { MINUTES_PER_TICK } from '../kernel.js';
import type { CommandHandler, SimulationSystem } from '../system.js';
import type { SimulationContext } from '../context.js';

/* ===================== 世界状态扩展 ===================== */

/** 电力域状态。纯可序列化；进状态哈希。 */
export interface PowerState {
  /** 已铺电缆的单元格（cellKey -> true）。与道路同构。 */
  cableCells: Record<string, true>;
  /**
   * 拓扑变更计数。铺缆/拆缆/建筑增删各 +1。
   * 它是领域事实（拓扑改过几次），不是缓存状态，因此可以安全进哈希。
   */
  topologyVersion: number;
  /** 储能当前存量（瓦·分钟，整数），键为实体 ID。 */
  storedWattMinutes: Record<string, number>;
  /** 上次结算被切断的消费者（实体 ID -> true）。滞回判定需要它跨 Tick 存在。 */
  shed: Record<string, true>;
  /**
   * 上一 Tick 的分量结算报表。
   *
   * 放进状态而不是模块变量：模块级的话多个内核实例会互相覆盖，快照往返也会
   * 丢失（UI 恢复存档后读到的是别人的电网）。它由确定性结算重算得出，
   * 因此进哈希不破坏确定性。
   */
  reports: PowerComponentReport[];
}

export function createEmptyPowerState(): PowerState {
  return { cableCells: {}, topologyVersion: 0, storedWattMinutes: {}, shed: {}, reports: [] };
}

/** WorldState 里的字段名（复用 G000 起就留好的 utilityNetworks 占位槽）。 */
export const POWER_DOMAIN = 'utilityNetworks';

interface WorldWithPower {
  [POWER_DOMAIN]: PowerState;
}
function powerOf(ctx: SimulationContext): PowerState {
  return (ctx.state as unknown as WorldWithPower)[POWER_DOMAIN];
}

/* ===================== 建筑的电力投影（依赖注入） ===================== */

/**
 * 电力所需的最小建筑定义投影。simulation-core 不 import game-data，
 * 由外壳把内容定义映射成本形状传入。
 */
export interface PowerSpec {
  readonly id: string;
  /** 额定发电（瓦，整数）。0 表示不发电。 */
  readonly outputWatts: number;
  /** 额定耗电（瓦，整数）。0 表示不耗电。 */
  readonly drawWatts: number;
  /** 储能充放功率上限（瓦，整数）。0 表示不储能。 */
  readonly storagePowerWatts: number;
  /** 储能容量（瓦·分钟，整数）。 */
  readonly storageCapacityWattMinutes: number;
  /**
   * 切断优先级 0..100。**数值越小越先被切**（0 = 最不重要）。
   * 生命支持类应给高值，装饰/工业类给低值。
   */
  readonly sheddingPriority: number;
}

export type GetPowerSpec = (defId: string) => PowerSpec | undefined;

/* ===================== 稳定拒绝码 ===================== */

export type PowerRejection =
  | 'CABLE_CELL_INVALID'
  | 'CABLE_ALREADY_EXISTS'
  | 'CABLE_NOT_FOUND'
  | 'CABLE_RESOURCES_INSUFFICIENT';

/** 每格电缆造价。 */
export const CABLE_COST: Readonly<Record<string, number>> = { metal: 4 };

/* ===================== 命令 ===================== */

export const LAY_CABLE = 'lay_cable';
export const REMOVE_CABLE = 'remove_cable';

export interface CableCellPayload {
  readonly cell: BuildCellId;
}

function isCellShape(c: unknown): c is BuildCellId {
  if (typeof c !== 'object' || c === null) return false;
  const o = c as Record<string, unknown>;
  return typeof o['layer'] === 'string'
    && Number.isInteger(o['ring']) && Number.isInteger(o['sector']);
}

/**
 * lay_cable — 在一个单元格上铺电缆。
 *
 * 与道路一样，电缆挂在格子上而不是建筑对之间：建筑通过「占用格与电缆格相邻」
 * 接入电网，与 G008 物流的可达性判定同构。电缆**不占建筑格**，因此已被建筑
 * 占用的格同样可以铺缆（这是与道路的唯一区别 —— 建筑必须能接到电）。
 */
export function createLayCableHandler(): CommandHandler<CableCellPayload> {
  return {
    type: LAY_CABLE,
    apply(ctx, payload) {
      if (!payload || !isCellShape(payload.cell)) {
        return { accepted: false, rejectionCode: 'CABLE_CELL_INVALID' };
      }
      const cell = getCell(ctx.state.spatial, payload.cell);
      if (!cell) return { accepted: false, rejectionCode: 'CABLE_CELL_INVALID' };

      const power = powerOf(ctx);
      const key = cellKey(payload.cell);
      if (power.cableCells[key]) {
        return { accepted: false, rejectionCode: 'CABLE_ALREADY_EXISTS' };
      }
      const stock = ctx.state.resources.stock as Record<string, number>;
      for (const [res, amount] of Object.entries(CABLE_COST)) {
        if ((stock[res] ?? 0) < amount) {
          return { accepted: false, rejectionCode: 'CABLE_RESOURCES_INSUFFICIENT' };
        }
      }

      for (const [res, amount] of Object.entries(CABLE_COST)) {
        stock[res] = (stock[res] ?? 0) - amount;
      }
      power.cableCells[key] = true;
      power.topologyVersion += 1;
      ctx.emit({ type: 'CableLaid', payload: { cell: { ...payload.cell } } });
      return { accepted: true };
    },
  };
}

export const SEED_CABLE = 'seed_cable';

/**
 * seed_cable — 场景预置电缆（既有殖民地的电网不是玩家铺的，不扣当期库存）。
 * 与 seed_building 同理：仍是命令，因此可重放、进哈希、被快照如实记录。
 */
export function createSeedCableHandler(): CommandHandler<CableCellPayload> {
  return {
    type: SEED_CABLE,
    apply(ctx, payload) {
      if (!payload || !isCellShape(payload.cell)) {
        return { accepted: false, rejectionCode: 'CABLE_CELL_INVALID' };
      }
      if (!getCell(ctx.state.spatial, payload.cell)) {
        return { accepted: false, rejectionCode: 'CABLE_CELL_INVALID' };
      }
      const power = powerOf(ctx);
      const key = cellKey(payload.cell);
      if (power.cableCells[key]) {
        return { accepted: false, rejectionCode: 'CABLE_ALREADY_EXISTS' };
      }
      power.cableCells[key] = true;
      power.topologyVersion += 1;
      return { accepted: true };
    },
  };
}

/** remove_cable — 拆除一格电缆，退还全部材料（电缆是可回收的线材）。 */
export function createRemoveCableHandler(): CommandHandler<CableCellPayload> {
  return {
    type: REMOVE_CABLE,
    apply(ctx, payload) {
      if (!payload || !isCellShape(payload.cell)) {
        return { accepted: false, rejectionCode: 'CABLE_CELL_INVALID' };
      }
      const power = powerOf(ctx);
      const key = cellKey(payload.cell);
      if (!power.cableCells[key]) {
        return { accepted: false, rejectionCode: 'CABLE_NOT_FOUND' };
      }
      const stock = ctx.state.resources.stock as Record<string, number>;
      for (const [res, amount] of Object.entries(CABLE_COST)) {
        stock[res] = (stock[res] ?? 0) + amount;
      }
      delete power.cableCells[key];
      power.topologyVersion += 1;
      ctx.emit({ type: 'CableRemoved', payload: { cell: { ...payload.cell } } });
      return { accepted: true };
    },
  };
}

/* ===================== 拓扑（复用共享层） ===================== */

/** 电力连通分量 = 共享层的网络分量（导管即电缆）。 */
export type PowerComponentTopology = NetworkComponent;
export type PowerTopology = NetworkTopology;

/** 测试用：按实例清空拓扑缓存。 */
export function clearPowerTopologyCache(power?: PowerState): void {
  if (power) clearNetworkCache(power);
}

/**
 * 取（必要时重建）电网拓扑。并查集、建筑接入、按实例缓存全部下沉到
 * utility/network.ts —— 那一层与「传输的是什么」无关，氧气与水复用同一套。
 *
 * 直接返回共享层的对象而不做字段改名适配：包装会在每次调用时新建对象，
 * 破坏「缓存命中即返回同一引用」这个可被测试观察的性质。
 */
export function powerTopology(
  map: SpatialMapData,
  power: PowerState,
  entities: EntityTableLike,
): PowerTopology {
  return networkTopology(power, map, power.cableCells, entities, power.topologyVersion);
}

/* ===================== 结算 ===================== */

export interface PowerConfig {
  /**
   * 恢复滞回（万分比）。被切断的消费者要重新通电，可用功率必须达到其需求的
   * 这个比例。纯等号会让供需刚好相等时一 Tick 通一 Tick 断，UI 与产出都闪。
   */
  readonly recoveryBasisPoints: number;
}
export const DEFAULT_POWER: PowerConfig = { recoveryBasisPoints: 11000 };

/** 单个分量的结算结果（UI 投影用）。 */
export interface PowerComponentReport {
  readonly id: string;
  readonly generationWatts: number;
  readonly demandWatts: number;
  /** 实际供给到消费者的功率（含储能放电）。 */
  readonly suppliedWatts: number;
  readonly storedWattMinutes: number;
  readonly shedEntityIds: readonly string[];
}

/**
 * 电力系统。
 *
 * 每 Tick 对**每个连通分量独立结算**（验收第 1 条）：
 *   发电 → 满足消费 → 盈余充电 / 缺口放电 → 仍不足则按优先级切断。
 */
export function createPowerSystem(
  getSpec: GetPowerSpec,
  cfg: PowerConfig = DEFAULT_POWER,
): SimulationSystem {
  return {
    id: 'power',
    update(ctx) {
      const power = powerOf(ctx);
      const topo = powerTopology(ctx.state.spatial, power, ctx.state.entities);
      const buildings = new Map<string, BuildingComponent>();
      for (const { entityId, building } of operationalBuildings(ctx.state.entities)) {
        buildings.set(entityId, building);
      }

      const reports: PowerComponentReport[] = [];
      const nextShed: Record<string, true> = {};

      for (const comp of topo.components) {
        let generation = 0;
        const consumers: Array<{ id: string; draw: number; priority: number; anchor: string }> = [];
        const stores: Array<{ id: string; powerLimit: number; capacity: number; anchor: string }> = [];

        // comp.entityIds 已排序 —— 整数累加与顺序无关，但排序让调试可复现
        for (const eid of comp.entityIds) {
          const b = buildings.get(eid);
          if (!b) continue;
          const spec = getSpec(b.defId);
          if (!spec) continue;
          if (spec.outputWatts > 0) generation += spec.outputWatts;
          const anchor = cellKey(b.anchor);          // 玩家给定的输入，与提交顺序无关
          if (spec.drawWatts > 0) {
            consumers.push({
              id: eid, draw: spec.drawWatts, priority: spec.sheddingPriority, anchor,
            });
          }
          if (spec.storagePowerWatts > 0 && spec.storageCapacityWattMinutes > 0) {
            stores.push({
              id: eid,
              powerLimit: spec.storagePowerWatts,
              capacity: spec.storageCapacityWattMinutes,
              anchor,
            });
          }
        }

        /* 考察顺序 = 保留优先次序：优先级降序 → 功率降序 → 锚点格升序。
         *
         * **绝不能用 entityId 做第二键**。它由 IdAllocator 按 ent_1, ent_2… 递增分配，
         * 本质上就是插入顺序的编码；配上贪心 first-fit 对「同优先级不同功率」的
         * 非对称性，会让同一张电网因建造顺序不同得出不同的供电结果（实测 300W
         * vs 700W，连 stateHash 都分叉）。锚点格是玩家给定的输入，与提交顺序无关。
         *
         * 功率降序（first-fit decreasing）是标准装箱启发式：既让结果只由输入决定，
         * 也比升序留下更高的实际利用率。 */
        const byKeepOrder = (
          a: { priority: number; draw: number; anchor: string },
          b: { priority: number; draw: number; anchor: string },
        ): number => (
          b.priority - a.priority
          || b.draw - a.draw
          || (a.anchor < b.anchor ? -1 : a.anchor > b.anchor ? 1 : 0)
        );
        consumers.sort(byKeepOrder);
        stores.sort((a, b) => (a.anchor < b.anchor ? -1 : a.anchor > b.anchor ? 1 : 0));

        const totalDemand = consumers.reduce((s, c) => s + c.draw, 0);

        /* 可放电总功率：受每台功率上限与当前存量共同限制（验收第 3 条的两个边界）。
           存量是瓦·分钟，要换算成本 Tick 能提供的瓦 —— 单位必须显式换算，
           不能因为 MINUTES_PER_TICK 眼下等于 1 就省掉。 */
        let dischargeAvailable = 0;
        for (const st of stores) {
          const stored = power.storedWattMinutes[st.id] ?? 0;
          dischargeAvailable += Math.min(st.powerLimit, Math.floor(stored / MINUTES_PER_TICK));
        }

        const available = generation + dischargeAvailable;

        /* --- 切断：从最低优先级开始丢，直到需求装得下 --- */
        const keep: typeof consumers = [];
        let kept = 0;
        // consumers 已按保留优先次序排好，正向遍历即可
        for (const c of consumers) {
          const wasShed = power.shed[c.id] === true;
          // 滞回：已被切断的，要恢复得留出余量；未被切断的，够用即可
          const need = wasShed
            ? Math.ceil((c.draw * cfg.recoveryBasisPoints) / 10000)
            : c.draw;
          if (kept + need <= available) { keep.push(c); kept += c.draw; }
          else nextShed[c.id] = true;
        }

        const suppliedWatts = kept;

        /* --- 储能充放 --- */
        let surplus = generation - suppliedWatts;                  // 瓦
        if (surplus > 0) {
          for (const st of stores) {
            if (surplus <= 0) break;
            const stored = power.storedWattMinutes[st.id] ?? 0;
            const room = st.capacity - stored;                      // 容量边界（瓦·分钟）
            // 功率边界与盈余都是瓦，乘 Tick 时长后才能与容量比较
            const moveWm = Math.min(surplus * MINUTES_PER_TICK, st.powerLimit * MINUTES_PER_TICK, room);
            if (moveWm > 0) {
              power.storedWattMinutes[st.id] = stored + moveWm;
              surplus -= Math.floor(moveWm / MINUTES_PER_TICK);
            }
          }
        } else if (surplus < 0) {
          let deficit = -surplus;                                   // 瓦
          for (const st of stores) {
            if (deficit <= 0) break;
            const stored = power.storedWattMinutes[st.id] ?? 0;
            const moveWm = Math.min(deficit * MINUTES_PER_TICK, st.powerLimit * MINUTES_PER_TICK, stored);
            if (moveWm > 0) {
              power.storedWattMinutes[st.id] = stored - moveWm;
              deficit -= Math.floor(moveWm / MINUTES_PER_TICK);
            }
          }
        }

        const storedTotal = stores.reduce(
          (s, st) => s + (power.storedWattMinutes[st.id] ?? 0), 0);

        reports.push({
          id: comp.id,
          generationWatts: generation,
          demandWatts: totalDemand,
          suppliedWatts,
          storedWattMinutes: storedTotal,
          shedEntityIds: consumers.filter((c) => nextShed[c.id]).map((c) => c.id).sort(),
        });
      }

      /* --- 切断集合变化才发事件（边沿触发，避免逐 Tick 刷屏） --- */
      for (const id of Object.keys(nextShed).sort()) {
        if (!power.shed[id]) ctx.emit({ type: 'PowerShed', payload: { entityId: id } });
      }
      for (const id of Object.keys(power.shed).sort()) {
        if (!nextShed[id]) ctx.emit({ type: 'PowerRestored', payload: { entityId: id } });
      }
      power.shed = nextShed;
      power.reports = reports;
    },
  };
}

/* ===================== UI 投影（纯函数，不改状态） ===================== */

export interface PowerSummary {
  readonly generationWatts: number;
  readonly demandWatts: number;
  readonly suppliedWatts: number;
  readonly storedWattMinutes: number;
  readonly componentCount: number;
  readonly shedCount: number;
}

/** 全网汇总（纯函数，读状态 —— 不依赖模块变量，多内核/快照恢复都正确）。 */
export function powerSummary(power: PowerState): PowerSummary {
  let generationWatts = 0, demandWatts = 0, suppliedWatts = 0, storedWattMinutes = 0, shedCount = 0;
  for (const r of power.reports) {
    generationWatts += r.generationWatts;
    demandWatts += r.demandWatts;
    suppliedWatts += r.suppliedWatts;
    storedWattMinutes += r.storedWattMinutes;
    shedCount += r.shedEntityIds.length;
  }
  return {
    generationWatts, demandWatts, suppliedWatts, storedWattMinutes,
    componentCount: power.reports.length, shedCount,
  };
}

/** 单栋建筑的供电状态投影。未接入电网返回 connected:false。 */
export function powerStatusOf(
  power: PowerState,
  map: SpatialMapData,
  entities: { order: readonly string[]; components: Record<string, Record<string, unknown>> },
  entityId: string,
): { connected: boolean; componentId: string | null; shed: boolean } {
  const topo = powerTopology(map, power, entities);
  const componentId = topo.componentOfEntity[entityId] ?? null;
  return {
    connected: componentId !== null,
    componentId,
    shed: power.shed[entityId] === true,
  };
}
